<template>
  <div class="p-4">
    <div
      class="sm:max-h-none sm:overflow-visible max-h-[500px] overflow-y-auto p-4 flex flex-col space-y-4"
    >
      <UForm
        :schema="schema"
        :state="state"
        class="space-y-4"
        @submit="onSubmit"
        @error="onError"
      >
        <UFormGroup label="Full Name" name="name">
          <UInput
            v-model="state.name"
            maxlength="50" 
            size="xl"
            placeholder="Enter receiver's full name"
            @input="sanitizeInput"
            @focus="onFocus"
            ref="nameInput"
            id="nameInput"
          />
        </UFormGroup>
        <UFormGroup label="Mobile Number" name="phone">
          <UInput
            type="number"
            v-model="state.phone"
            size="xl"
            placeholder="Enter receiver's 10-digit mobile number"
            @keydown="handleMobileInput"
            @focus="onFocus"
            ref="mobileInput"
            id="mobileInput"
          />
        </UFormGroup>
        <UFormGroup label="Email" name="email">
          <UInput
            type="email"
            v-model="state.email"
            maxlength="50"
            size="xl"
            placeholder="Enter receiver's email"
            @focus="onFocus"
            ref="emailInput"
            id="emailInput"
          />
        </UFormGroup>
        <UFormGroup
          label="Flat / House number / Floor / Building"
          name="address_line1"
        >
          <UInput
            v-model="state.address_line1"
            maxlength="50"
            size="xl"
            placeholder="Enter details"
            @focus="onFocus"
            ref="houseNumber"
            id="houseNumber"
          />
        </UFormGroup>
        <UFormGroup label="Street / Road" name="address_line2">
          <UInput
            v-model="state.address_line2"
            maxlength="100"
            size="xl"
            placeholder="Enter details"
            @focus="onFocus"
            ref="street"
            id="street"
          />
        </UFormGroup>
        <UFormGroup label="Area / Sector / Locality" name="address_line3">
          <UInput
            v-model="state.address_line3"
            maxlength="100"
            size="xl"
            placeholder="Enter details"
            class="bg-gray-200"
            @focus="onFocus"
            ref="locality"
            id="locality"
          />
        </UFormGroup>
        <UFormGroup label="PIN Code" name="area_code">
          <UInput
            type="number"
            v-model="state.area_code"
            size="xl"
            placeholder="Enter a 6-digit PIN code"
            class="bg-gray-200"
            @keydown="handlePincodeInput"
            @focus="onFocus"
            ref="pinCode"
            id="pinCode"
          />
        </UFormGroup>
        <UFormGroup label="City" name="city">
          <UInput
            v-model="state.city"
            maxlength="30"
            size="xl"
            placeholder="Enter your city"
            class="bg-gray-200"
            @input="sanitizeInput"
            @focus="onFocus"
            ref="cityInput"
            id="cityInput"
          />
        </UFormGroup>
        <UFormGroup label="State" name="state">
          <UInput
            v-model="state.state"
            maxlength="30"
            size="xl"
            placeholder="Enter your state"
            class="bg-gray-200"
            @input="sanitizeInput"
            @focus="onFocus"
            ref="stateInput"
            id="stateInput"
          />
        </UFormGroup>
        <UFormGroup label="Save address as" name="label">
          <div class="inline-flex items-center gap-2 mt-2">
            <UBadge
              :variant="state.label === 'home' ? 'solid' : 'outline'"
              size="md"
              @click="state.label = 'home'"
              >Home</UBadge
            >
            <UBadge
              :variant="state.label === 'office' ? 'solid' : 'outline'"
              size="md"
              @click="state.label = 'office'"
              >Office</UBadge
            >
            <UBadge
              :variant="state.label === 'other' ? 'solid' : 'outline'"
              size="md"
              @click="state.label = 'other'"
              >Other</UBadge
            >
          </div>
        </UFormGroup>
        <UFormGroup label="Save as" name="customLabel" v-if="state.label.toLowerCase() === 'other'">
                <UInput v-model="state.customLabel" size="xl" class="bg-gray-200"
                  @input="sanitizeInput" @focus="onFocus" ref="stateInput" id="stateInput" maxlength="22" />
                  <p v-if="labelError" class="text-sm pt-3 text-red-500">Please specify this field</p>
        </UFormGroup>
        <div class="flex flex-col items-start py-5 pb-20">
          <!-- Added padding-bottom to ensure submit button stays visible -->
          <UButton
            type="submit"
            size="md"
            label="Save Address"
            class="md:w-2/5 w-full"
            :loading="isLoading"
            :disabled="disableButton"          
          />
        </div>
      </UForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as v from "valibot";
import type { FormSubmitEvent, FormErrorEvent } from "#ui/types";
const validLabels = ['home', 'office', 'other'];
const props = defineProps(["label", "data", "gps"]);
const address = props?.data?.address
const location = useLocation();

const addressRegex = /^[A-Za-z0-9 ,.\-\/#()&+]+$/;
const forbiddenAddressPatterns = /(\/\/|https?:\/\/|www\.|\.(com|in|net|org|co|io|gov|edu)\b)/i;


const addressValidator = v.pipe(
  v.string(),
  v.minLength(1, "This field cannot be left blank"),
  v.regex(
    addressRegex,
    "Only letters, numbers, spaces, and , . - / # ( ) & + are allowed."
  ),
  v.check(
    (value: string) => !forbiddenAddressPatterns.test(value),
    "Invalid format. Please remove // or web-related text such as http or www."
  )
);


let labelError = ref(false);
// import pincodeData from '../../resources/pincodes.json';
const noHtml = (field: string) =>
  v.custom<string>(
    (value) => !/<[^>]+>/g.test(value as string),
    `Please enter valid ${field}`
  );

const schema = v.object({
  label: v.pipe(v.string(), v.minLength(1, "Please select address label")),
  customLabel: v.optional(v.pipe(v.string(), noHtml("Label"))),
  name: v.pipe(v.string(), v.minLength(1, "This field cannot be left blank"),v.regex(/^[A-Za-z\s.]+$/, "Name can contain only letters and spaces")),
   phone: v.pipe(
    v.any(),
    v.transform((input) => input.toString()),
    v.minLength(10, "Provide 10-digit mobile number"),
    v.maxLength(10, "Provide 10-digit mobile number")
  ),
email: v.pipe(
    v.string(),
    v.minLength(1, "Please enter a email address"),
    v.regex(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
      "Please enter a valid email address"
    )
  ),
  address_line1: addressValidator,
  address_line2: addressValidator,
  address_line3: addressValidator,
  city: addressValidator,
  state: addressValidator,
  
  area_code: v.pipe(
    v.any(),
    v.transform((input) => input.toString()),
    v.minLength(6, "Provide 6-digit PIN code"),
    v.maxLength(6, "Provide 6-digit PIN code")
  ),
});
type Schema = v.InferOutput<typeof schema>;
const initialFormState = ref<Schema | null>(null);
import { reactive, ref, onMounted, computed, watch } from "vue";

const state = reactive({
  label: "",
  customLabel:  !validLabels.includes(address?.label) ? address?.label || '' : '',
  name: "",
  phone: "",
  email: "",
  address_line1: "",
  address_line2: "",
  address_line3: "",
  city: "",
  state: "",
  area_code: "",
});

onMounted(() => {
  const checkout = useCheckout();
  // let obj = {
  const providedLabel = address?.label;
  state.label = providedLabel ? (validLabels.includes(providedLabel) ? providedLabel : 'other'): '';
  state.name = address?.contact?.name || "";
  state.phone = address?.contact?.phone || "";
  state.email = address?.contact?.email || "";
  state.address_line1 = reset ? "" : address?.door || location?.address_line1 || "";
  state.address_line2 = reset ? "" : address?.building || location?.address_line2 || "";
  state.address_line3 = reset ? "" : address?.locality || location?.address_line3 || "";
  state.city = reset ? "" : address?.city || location?.city?.name || "";
  state.state = reset ? "" : address?.state || location?.state?.name || "";
  state.area_code = reset ? "" : address?.area_code || location?.area_code || "";
  state.gps = location.gps;
  // };
  initialFormState.value = { ...state };
});

const isLoading = ref(false);
const emit = defineEmits(["success",'dirty']);
const reset = props?.data?.fromPage == 'ProfileAddress';
const disableButton = ref(false);

const sanitizeInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const sanitizedValue = input.value.replace(/[^a-zA-Z0-9\s]/g, "");

  const fieldName = input.getAttribute("name");
  if (fieldName && fieldName in state) {
    (state as any)[fieldName] = sanitizedValue;
  }
};

const handleMobileInput = (event: KeyboardEvent) => {
  const mobileValue = state.phone.toString().replace(/\D/g, "");
  const validMobileRegex = /^[0-9]{10}$/;
  if (mobileValue.length > 9 && event.key !== "Backspace") {
    event.preventDefault();
  }
};

const handlePincodeInput = (event: KeyboardEvent) => {
  if (state.area_code) {
    const value = state.area_code.toString();
    if (value.length > 5 && event.key !== "Backspace") {
      event.preventDefault();
      return;
    }
  }
};

async function onSubmit(event: FormSubmitEvent<Schema>) {
  // const foundPincode = await pincodeData.array.forEach(item => {
  //   if(item.pincode === event.data?.area_code)
  //     {
  //       return true
  //     }else{
  //       return false
  //     }
  // });
  // console.log("pincode validation::",foundPincode)
  if(event.data?.label.toLowerCase() === 'other' && (!event.data?.customLabel || event.data?.customLabel.trim() === '')){
    labelError.value = true
    return;
  }
  isLoading.value = true;
  let labeloption
  if(event.data?.label && event.data?.label.toLowerCase() === 'other'){
    labeloption = event.data?.customLabel
  }else{
    labeloption = event.data?.label
  }
  try {
    const latitude = Number(props?.gps?.lat ?? props?.gps?.latitude);
    const longitude = Number(props?.gps?.lng ?? props?.gps?.longitude);
    let payload = {
      label: labeloption,
      door: event.data?.address_line1,
      building: event.data?.address_line2,
      locality: event.data?.address_line3,
      city: event.data?.city,
      state: event.data?.state,
      area_code: event.data?.area_code,
      contact: {
        name: event.data?.name,
        phone: `${event.data?.phone}`,
        email: event.data?.email.toLowerCase(),
      },
    };
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      payload.gps = {
        latitude,
        longitude,
      };
    }
    if (address) {
        payload.id = address.id;
      }
    const response = await useAPIHandler("post", "/account/addresses", {
      body: payload,
      auth: true,
    });
    if (response.data.toLowerCase() === "success") {
      emit("success", payload);
    }
  } catch (err) {
    console.log(err);
  } finally {
    isLoading.value = false;
  }
}

async function onError(event: FormErrorEvent) {
  if (event?.errors?.[0]?.id) {
    const element = document.getElementById(event.errors[0].id)
    element?.focus()
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const onFocus = (event: FocusEvent): void => {
  const target = event.target;
  
  if (target instanceof HTMLElement) {
    scrollToInput(target);
  }
};

const scrollToInput = (element: HTMLElement) => {
  if (element instanceof HTMLElement) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
};

const pincode = computed(() => state.area_code);
watch(pincode, async (newValue) => {
  if (newValue) {
    const value = newValue.toString();
    if(value.length > 5) {
      const validate: any = await validatePinCode(value);
      if(validate.status == 1) {
       state.city = validate.data.district;
       state.state = validate.data.state;
       disableButton.value = false;
      } else if (validate.status == 0) {
        disableButton.value = true;
      }
    }
  }
})

watch(
  () => ({ ...state }),
  (newState) => {
    if (initialFormState.value) {
     
      const isDirty = !isEqual(newState, initialFormState.value);
      emit('dirty', isDirty);
    }
  },
  { deep: true,immediate: true }
);
</script>
